import React, { useContext, useState } from 'react';
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import { TableHeaderContext } from '../../Contexts/TableHeaderContext';
import SelectIndustryFilter from '../RefineSearchComponents/SelectIndustryFilter';
import StageFilter from '../RefineSearchComponents/StageFilter';
import { useLocation } from 'react-router';
import FirmTypeFilter from '../RefineSearchComponents/FirmTypeFilter';
import { IoMdArrowDropdown, IoMdArrowDropright } from "react-icons/io";

/**
 * The `ActiveInvestorRefineSearch` function is a React component that renders a refine search form
 * with options for industries, stages, and firm types, and allows the user to apply or reset the
 * selected filters.
 * @returns The function `ActiveInvestorRefineSearch` returns a JSX element, which represents the UI of
 * the refine search component.
 */
function ActiveInvestorRefineSearch({ customPathname, menu }) {
    const { setrefineSearchFilters } = useContext(PeFilterContext);
    const { setSelectedPage } = useContext(TableHeaderContext);
    const [select, setSelected] = useState(0);

    const [IndustryOptions, setIndustryOptions] = useState([]);
    const [StageOptions, setStageOptions] = useState([]);
    const [FirmTypeOptions, setFirmTypeOptions] = useState([]);
    const [FirmSubTypeOptions, setFirmSubTypeOptions] = useState([]);
    const location = useLocation();

    // State for managing which filter section is active
    const [activeFilterSection, setActiveFilterSection] = useState("deal");
    
    // State for managing which individual filter popup is open
    const [openFilterPopup, setOpenFilterPopup] = useState(null);

    // Helper functions to count applied filters for each section
    const getDealFiltersCount = () => {
        let count = 0;
        if (IndustryOptions.some(option => option.isChecked)) count++;
        if (menu === "investments" && StageOptions.some(option => option.isChecked)) count++;
        if (FirmTypeOptions.some(option => option.isChecked)) count++;
        return count;
    };

    /**
     * The function `applyFilter` updates the refine search filters by selecting the checked options for
     * industries, stages, and firm types.
     */
    const applyFilter = () => {
        const selectedIndustries = IndustryOptions.filter(industry => industry.isChecked);
        const selectedStages = StageOptions.filter(option => option.isChecked);
        const selectedFirms = FirmTypeOptions?.filter(option => option.isChecked);
        const selectedFirmSubTypes = FirmSubTypeOptions?.filter(option => option.isChecked);
        const firmTypeIds = selectedFirms?.filter(firm => firm.isChecked).map(firm => firm.id) ?? [];

        let filteredFirmSubTypes = null;

        const excludedIds = [4, 5, 13, 14];

        if (firmTypeIds.includes(1) || firmTypeIds.includes(2)) {
            filteredFirmSubTypes = selectedFirmSubTypes?.filter(sub => {
                const isPE = sub.name?.includes('(PE)') || sub.originalName?.includes('(PE)');
                const isVC = sub.name?.includes('(VC)') || sub.originalName?.includes('(VC)');

                if (firmTypeIds.includes(1) && firmTypeIds.includes(2)) {
                    return isPE || isVC;
                } else if (firmTypeIds.includes(1)) {
                    return isPE;
                } else if (firmTypeIds.includes(2)) {
                    return isVC;
                }
                return false;
            })?.map(sub => {
                if (!excludedIds.includes(sub.id)) {
                    return {
                        ...sub,
                        name: sub.name.replace(/\((PE|VC)\)/, ''),
                    };
                }
                return sub;
            }) ?? null;

            if (filteredFirmSubTypes.length === 0) {
                filteredFirmSubTypes = null;
            }
        }

        setrefineSearchFilters(prev => ({
            ...prev,
            [customPathname]: {
                ...prev[customPathname],
                industry: selectedIndustries,
                stage: selectedStages,
                firmType: selectedFirms,
                firm_Sub_type: filteredFirmSubTypes,
            },
        }));
    };

    /**
     * The function `resetFilter` resets a specific filter in the `refineSearchFilters` object by setting
     * its value to an empty object.
     */
    const resetFilter = () => {
        setrefineSearchFilters(prev => ({
            ...prev,
            [customPathname]: {}
        }));
    };

    // Submitting Refine Search Form 
    /**
     * The function `refineSearchFilter` is used to apply a filter and reset the selected page to 1.
     * @param event - The event parameter is an object that represents the event that triggered the
     * function. It contains information about the event, such as the type of event and any data
     * associated with it. In this case, the function is using the event.preventDefault() method to
     * prevent the default behavior of the event, which is typically
     */
    const refineSearchFilter = (event) => {
        event.preventDefault();
        setSelectedPage(1);
        applyFilter();
        setSelected(false);
    };

    /**
     * The `onResetClick` function resets the selected page to 1 and calls the `resetFilter` function.
     * @param event - The event parameter is an object that represents the event that triggered the
     * function. In this case, it is the click event that occurred when the user clicked on a reset
     * button.
     */
    const onResetClick = (event) => {
        event.preventDefault();
        setSelectedPage(1);
        resetFilter();
    };

    /**
     * The function `accordionClick` sets the selected value to 0 if it is equal to `select`, otherwise
     * it sets it to the provided value.
     * @param val - The `val` parameter is a value that is passed to the `accordionClick` function. It is
     * used to determine whether the accordion should be selected or deselected.
     */
    const accordionClick = (val) => {
        if (val === select) {
            setSelected(0);
        } else {
            setSelected(val);
        }
    };

    return (
        <div className="bg-white">
            <div className="h-full no-scrollbar">
                <form onSubmit={refineSearchFilter} onReset={onResetClick} className="h-full w-full">
                    <main className="h-full w-full py-4">
                        <div className="grid grid-cols-8 gap-4 h-full">
                            {/* Left column - Headers */}
                            <div className="h-full col-span-2 flex flex-col gap-2 border-r border-gray-300 pr-4">
                                <div 
                                    className={`cursor-pointer p-3 rounded-lg transition-colors ${
                                        activeFilterSection === 'deal' ? 'bg-[#BC8B20] text-white' : 'hover:bg-gray-100'
                                    }`}
                                    onClick={() => setActiveFilterSection(activeFilterSection === 'deal' ? null : 'deal')}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium">Deal</span>
                                            {getDealFiltersCount() > 0 && (
                                                <span className={`text-xs rounded-full px-2 py-1 min-w-[20px] text-center ${activeFilterSection === 'deal' ? 'bg-white text-[#bc8b20]' : 'bg-[#bc8b20] text-white'}`}>
                                                    {getDealFiltersCount()}
                                                </span>
                                            )}
                                        </div>
                                        {activeFilterSection === 'deal' ? <IoMdArrowDropdown /> : <IoMdArrowDropright />}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Right column - Filters */}
                            <div className="h-full col-span-6 overflow-y-auto">
                                <div className={`grid grid-cols-2 gap-4 gap-y-8 ${activeFilterSection === 'deal' ? '' : 'hidden'}`}>
                                    <SelectIndustryFilter
                                        IndustryOptions={IndustryOptions}
                                        setIndustryOptions={setIndustryOptions}
                                        customPathname={customPathname}
                                        isPopupOpen={openFilterPopup === 'industry'}
                                        setIsPopupOpen={(isOpen) => setOpenFilterPopup(isOpen ? 'industry' : null)}
                                    />
                                    {menu === "investments" && (
                                        <StageFilter
                                            StageOptions={StageOptions}
                                            customPathname={customPathname}
                                            setStageOptions={setStageOptions}
                                            accordionClick={() => accordionClick(3)}
                                            open={select === 3}
                                            menu={menu}
                                            isPopupOpen={openFilterPopup === 'stage'}
                                            setIsPopupOpen={(isOpen) => setOpenFilterPopup(isOpen ? 'stage' : null)}
                                        />
                                    )}
                                    <FirmTypeFilter
                                        FirmTypeOptions={FirmTypeOptions}
                                        setFirmTypeOptions={setFirmTypeOptions}
                                        FirmSubTypeOptions={FirmSubTypeOptions}  
                                        setFirmSubTypeOptions={setFirmSubTypeOptions}
                                        customPathname={customPathname}
                                        dataPath={location?.pathname.includes("angel") ? "angel" : "investments"}
                                        isPopupOpen={openFilterPopup === 'firmType'}
                                        setIsPopupOpen={(isOpen) => setOpenFilterPopup(isOpen ? 'firmType' : null)}
                                    />
                                </div>
                                
                                <div className={`flex items-center justify-center h-40 text-gray-500 ${activeFilterSection ? 'hidden' : ''}`}>
                                    <p>Select a filter category from the left to view available filters</p>
                                </div>
                            </div>
                        </div>
                    </main>
                </form>
            </div>
        </div>
    );
}

export default ActiveInvestorRefineSearch;